import { ApiError } from '@shared/api/client';
import { resolveErrorCopy, type ErrorCopy } from '@shared/errors/catalog';

import { authErrors, AUTH_FIELD_FOR_CODE } from './errors';

/**
 * One failure, turned into what the screen renders.
 *
 * The copy is resolved from `error_code` and **never** from `message` — the rule
 * the whole console shares, applied here once for all three screens.
 *
 * **No `X-Request-ID`, here or anywhere else in this console.** What it drew
 * was a thirty-two character hex string under a refusal — an identifier for a
 * conversation the reader cannot start. The id is still on the response and
 * still in the log line the server wrote; it is only never drawn.
 *
 * A `NetworkError` reaches this too. It carries no code and no status, so it
 * falls to the shared unknown copy and stays neutral: the operator's connection
 * dropping is not a failure of theirs.
 */
export interface FormFailure {
  message: string;
  tone: 'neutral' | 'danger';
}

/** `warning` is a field-level tone; a banner has two. */
function toBannerTone(copy: ErrorCopy): 'neutral' | 'danger' {
  return copy.tone === 'neutral' ? 'neutral' : copy.tone === 'danger' ? 'danger' : 'neutral';
}

export function toFormFailure(error: unknown): FormFailure {
  if (error instanceof ApiError) {
    const copy = resolveErrorCopy(error.code, error.status, authErrors);
    return { message: copy.message, tone: toBannerTone(copy) };
  }

  const copy = resolveErrorCopy(undefined, 0, authErrors);
  return { message: copy.message, tone: 'neutral' };
}

/**
 * The field a failure belongs under, if any.
 *
 * `details[]` is read first, because the service names the field itself where
 * it can. Where it carries none — a top-level `422 AUTH_OTP_INVALID` has no
 * detail row — the code's own mapping is the fallback, so the sentence still
 * lands on the control the operator has to change rather than at the top of a
 * form with one field in it.
 *
 * A failure with no field mapping is deliberately left to the banner: an
 * account lockout is not a property of the code box.
 */
export function toFieldFailure(error: unknown): { field: string; message: string } | null {
  if (!(error instanceof ApiError)) return null;

  for (const detail of error.details ?? []) {
    const field = detail.field ?? AUTH_FIELD_FOR_CODE[detail.code];
    if (!field) continue;
    const copy = resolveErrorCopy(detail.code, error.status, authErrors);
    return { field, message: copy.message };
  }

  const field = AUTH_FIELD_FOR_CODE[error.code];
  if (!field) return null;

  return { field, message: resolveErrorCopy(error.code, error.status, authErrors).message };
}

/**
 * The seconds a `429` says to wait. The body integer is authoritative and the
 * header mirrors it; the client backs off from the body per the platform's rate
 * limit contract.
 */
export function toRetryAfter(error: unknown): number | null {
  return error instanceof ApiError && typeof error.retryAfter === 'number' ? error.retryAfter : null;
}
